import { CSSProperties } from 'react'
import { Avatar as AntAvatar } from 'antd'
import { usePresence } from '@y-sweet/react'

import { Avatar, Tooltip, Dropdown, Menu, MenuItem } from '@pepper/ui'

import { useDocPermissions } from '../../hooks/useDocPermissions'

import styles from './_.module.css'
import type { PresenceData } from '../../types/Editor.types'

const MAX_VISIBLE_AVATARS = 4

function PresenceAvatar({
  presence,
  size,
  className,
}: {
  presence: PresenceData
  size: number
  className: string
}): JSX.Element {
  const initial = presence.user.name?.[0] ?? '?'
  return (
    <Avatar
      showBorderColor
      showOutline
      nameChar={initial}
      size={size}
      src={presence.user.avatar ?? undefined}
      className={className}
      style={{ '--user-color': presence.user.color } as CSSProperties}
    >
      {initial}
    </Avatar>
  )
}

function CollaborationAvatars(): JSX.Element | null {
  const presenceMap = usePresence<PresenceData>()
  const {
    currentUser: { id: selfId },
  } = useDocPermissions()

  // y-sweet keys presence by connection, so a user with multiple tabs
  // appears multiple times. dedupe by user id, keeping the first entry.
  const othersById = new Map<string, PresenceData>()
  for (const p of presenceMap.values()) {
    if (p.id !== selfId && !othersById.has(p.id)) othersById.set(p.id, p)
  }
  const others = Array.from(othersById.values())

  if (others.length === 0) return null

  const visible = others.slice(0, MAX_VISIBLE_AVATARS)
  const hiddenCount = others.length - visible.length

  if (visible.length === 1) {
    const only = visible[0]
    return (
      <Tooltip title={only.user.name} placement="bottom">
        <div className={styles.peCollaboratorsWrap}>
          <PresenceAvatar
            presence={only}
            size={32}
            className={styles.editorAvatarView}
          />
        </div>
      </Tooltip>
    )
  }

  const dropdownMenu = (
    <Menu className={styles.dropdownEditorsList}>
      {others.map(p => (
        <MenuItem key={p.id} className={styles.editorNameAvatarContainer}>
          <PresenceAvatar
            presence={p}
            size={24}
            className={styles.editorDropdownAvatar}
          />
          <div className={styles.editorName}>{p.user.name}</div>
        </MenuItem>
      ))}
    </Menu>
  )

  return (
    <Dropdown
      overlay={dropdownMenu}
      className={styles.currentEditorsListDropdown}
    >
      <div className={styles.peCollaboratorsWrap}>
        <AntAvatar.Group>
          {visible.map(p => (
            <PresenceAvatar
              key={p.id}
              presence={p}
              size={32}
              className={styles.editorAvatarView}
            />
          ))}
        </AntAvatar.Group>
        {hiddenCount > 0 && (
          <div className={styles.remainingEditors}>+{hiddenCount}</div>
        )}
      </div>
    </Dropdown>
  )
}

export default CollaborationAvatars
